import * as React from 'react';
import { useFormContext, type FieldPath, type FieldValues } from 'react-hook-form';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from './Form';
import { Input } from '@/components/ui/input';
import { toMinor, fromMinor, toMinorUnits, fromMinorUnits, moneyHint } from '@/lib/money';

interface MoneyInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> {
  /** Current value in minor units (number or digit-string). */
  value: number | string | null | undefined;
  /** Emits the new value as a minor-unit digit string; '' when the box is blank
   * (callers map that to undefined for optional fields). */
  onChange: (minor: string) => void;
  /**
   * ISO currency — drives decimal places and the preview symbol.
   *
   * REQUIRED, deliberately. It used to default to 'THB', and the wallet adjust
   * dialog — the most money-critical input in the console — passed nothing: an
   * operator crediting a JPY member ¥2,000 typed 2000 and the ledger received
   * 200000 minor units, ¥200,000. Nothing downstream can catch that, because
   * both sides agree on the number; the scale was lost at the boundary. A type
   * error at the call site is the only reliable way to stop it recurring
   * (BO-05).
   *
   * `null` is explicitly allowed and means "there is no currency here": the box
   * shows and takes the stored integer minor units themselves, with no exponent
   * applied in either direction, no symbol hint and no code badge. That is a row
   * with no brand behind it — today, a coupon before svc-promo has stamped its
   * `agent_id`, or one on a brand whose org row records no currency. The same
   * contract `QuickAmountsInput` carries for the platform finance-config scope,
   * which is a different component.
   *
   * It is NOT "we have not looked the currency up yet". A substituted currency
   * still prices whatever the operator TYPES under it, and no component can undo
   * that, so callers must render that state as absence and mount no box at all.
   * What a late or changed currency can no longer do is rewrite a figure nobody
   * typed: the effect below RE-READS the stored integer at the new exponent and
   * leaves the row alone. That is the only reading that cannot silently move
   * money — the figure on screen changes, visibly, and the value does not. Four
   * screens shipped the other way and each turned a stored 12345 into 1234500
   * (#558, #561, #562, #563), so it is not an option any caller can take: digits
   * the operator TYPED are re-priced, digits they did not are re-read, and no
   * prop switches between them.
   *
   * That does not make a `key` on the denomination redundant, and an existing one
   * must not be removed on the strength of it. Two cases stay the key's alone:
   * digits the operator TYPES under `currency={null}` before a brand is picked,
   * which provenance correctly calls theirs and would therefore re-price at the
   * arriving exponent (`PromotionFormDialog`, #563); and a box whose SUBJECT
   * changes rather than its denomination — the member drawer swapping wallets
   * under `AdjustDialog`, where the previous member's figure must be discarded
   * rather than re-read.
   */
  currency: string | null;
  /** Render the "฿100.50" preview line under the input. Default true. */
  showHint?: boolean;
  /** Render the suffix badge inside the input. Default true. */
  showSuffix?: boolean;
  /**
   * What the suffix badge says when `currency` is null, in place of the currency
   * code — "minor units", localised by the caller.
   *
   * Without it a currency-less box is visually an ordinary money box whose scale
   * has silently changed by 100× for anyone with muscle memory, and the only
   * correction is whatever the caller wrote UNDER it, which is the element an
   * operator skips. The badge sits exactly where the currency code sits, inside
   * the input the eye is already on.
   *
   * Optional, so a caller that explains the scale some other way is not forced
   * to carry a badge. `CouponForm` is the only null-currency caller today and it
   * passes one.
   */
  minorUnitsLabel?: React.ReactNode;
}

/**
 * Money input. Staff type a normal decimal amount ("100.50"); the value read
 * and emitted is integer minor units ("10050") to match the wire contract
 * (`Money = t.Integer`). A local text state holds what's typed so intermediate
 * input ("100.", "0.0") isn't clobbered by round-tripping through minor units.
 */
export function MoneyInput({
  value,
  onChange,
  currency,
  showHint = true,
  showSuffix = true,
  minorUnitsLabel,
  className,
  ...rest
}: MoneyInputProps) {
  const extMinor = value === undefined || value === null || value === '' ? 0 : Number(value);
  const readMinor = (typed: string) =>
    currency === null ? toMinorUnits(typed) : toMinor(typed, currency);
  const display = (m: number) =>
    m === 0 ? '' : currency === null ? fromMinorUnits(m) : fromMinor(m, currency);
  const [text, setText] = React.useState(() => display(extMinor));
  const seenCurrency = React.useRef(currency);
  /**
   * Where the digits on screen came from, which is the whole question when the
   * denomination moves: the operator's keystrokes, or this component's own
   * rendering of the stored value. Set on input, cleared whenever the text is
   * re-seeded from outside — so it answers "since the last time the form told me
   * what to show, has anyone typed?", which is exactly the question the effect
   * asks.
   */
  const typedSinceSeed = React.useRef(false);

  // Re-seed the visible text when the value changes from outside (form reset,
  // edit-existing) and no longer matches what's typed. Comparing via toMinor
  // avoids fighting the user mid-keystroke ("100." keeps its trailing dot).
  React.useEffect(() => {
    if (seenCurrency.current !== currency) {
      seenCurrency.current = currency;
      // The operator's digits are the truth once a denomination is chosen under
      // them: type 1000 under a THB box, switch the box to JPY, and 1000 must
      // mean ¥1,000 — not ¥100,000, which is what keeping the stored integer
      // would leave behind. So re-derive from what they can SEE and tell the
      // form.
      if (typedSinceSeed.current) {
        onChange(text.trim() === '' ? '' : String(readMinor(text)));
        return;
      }
      // Nobody typed these. They are the stored integer rendered under an
      // exponent that has just been corrected, so the integer is the truth and
      // the digits are what must move. Re-pricing here is the bug four batches
      // of this sweep fixed one screen at a time: a stored ¥12,345 read as
      // "123.45" under a THB guess and re-emitted as 12345 → 1234500 the moment
      // JPY landed, with the number on screen never moving and nothing
      // downstream able to tell. Re-read, emit nothing, leave the row alone.
      setText(display(extMinor));
      return;
    }
    if (readMinor(text) !== extMinor) {
      setText(display(extMinor));
      typedSinceSeed.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extMinor, currency]);

  const hint = showHint && currency !== null ? moneyHint(extMinor, currency) : '';
  const noCurrencyBadge =
    minorUnitsLabel === undefined || minorUnitsLabel === null || minorUnitsLabel === ''
      ? null
      : minorUnitsLabel;
  const suffix = !showSuffix ? null : currency !== null ? currency : noCurrencyBadge;
  // A word needs more room than a 3-letter code, and the badge overlaps what is
  // typed if the input does not reserve it.
  const suffixPad = suffix === null ? '' : currency !== null ? 'pr-14' : 'pr-28';

  return (
    <>
      <div className="relative">
        <Input
          {...rest}
          type="text"
          inputMode={currency === null ? 'numeric' : 'decimal'}
          value={text}
          onChange={(e) => {
            const raw = e.target.value;
            typedSinceSeed.current = true;
            setText(raw);
            onChange(raw.trim() === '' ? '' : String(readMinor(raw)));
          }}
          className={`${suffixPad} ${className ?? ''}`}
        />
        {suffix !== null && (
          <span
            className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground ${
              currency !== null ? 'text-sm' : 'text-xs'
            }`}
          >
            {suffix}
          </span>
        )}
      </div>
      {hint && <p className="text-xs text-muted-foreground tabular-nums">{hint}</p>}
    </>
  );
}

interface FormMoneyInputProps<TValues extends FieldValues>
  extends Omit<MoneyInputProps, 'value' | 'onChange'> {
  name: FieldPath<TValues>;
  label?: React.ReactNode;
  description?: React.ReactNode;
}

/** react-hook-form wrapper around {@link MoneyInput}. Stores minor units (a
 * number) in the field value, so resolvers and API calls need no change. */
export function FormMoneyInput<TValues extends FieldValues>({
  name,
  label,
  description,
  ...moneyProps
}: FormMoneyInputProps<TValues>) {
  const { control } = useFormContext<TValues>();
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          {label !== undefined && <FormLabel>{label}</FormLabel>}
          <FormControl>
            <MoneyInput
              {...moneyProps}
              value={field.value as number | string | null | undefined}
              onChange={(minor) => field.onChange(minor === '' ? 0 : Number(minor))}
            />
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
